import { filter } from '@atlaskit/adf-utils/traverse';

import type {
	PreloadFailureReason,
	PreloadFailureResult,
} from '@confluence/fabric-extension-lib/entry-points/fabric-extension-lib-types.ts';
import { supportedExtensions } from '@confluence/fabric-extension-lib/entry-points/extensionConstants.ts';
import { getMultiMacroQueryBlocklistFF } from '@confluence/fabric-extension-queries/entry-points/useMultiMacroQuery.ts';
import {
	MultiMacroQueryWithAppKeyQuery,
	type multiMacroQueryWithAppKeyQuery$data,
	type multiMacroQueryWithAppKeyQuery$variables,
} from '@confluence/fabric-extension-queries/entry-points/multiMacroQueryWithAppKeyQuery.ts';
import {
	SLOW_QUERY_DEFAULT_TIMEOUT,
	slowQueryGuard,
} from '@confluence/query-preloader-tools/entry-points/slowQueryGuard.ts';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

import { DEFAULT_PRELOAD_COUNT } from './preloader-constants.ts';

const extractExtensions = (adf: any) => filter(adf, (node) => supportedExtensions.has(node.type));

export const hasExtensions = (contentNodes: any): boolean => {
	try {
		const adf = JSON.parse(contentNodes[0].body.dynamic.value);
		const extensions = extractExtensions(adf);
		return extensions.length > 0;
	} catch {
		return false;
	}
};

const failureIfNotSuccess = (reason: PreloadFailureReason) => (success: boolean) =>
	success ? undefined : { MacrosQuery_extensions: reason };

const preloadMacrosSSRAndReturnTopXFailed = async (
	contentId: string,
	contentNodesOrPromise?: any,
) => {
	const blocklist = getMultiMacroQueryBlocklistFF();

	const isContentNodePromise = typeof contentNodesOrPromise?.then === 'function';
	if (contentNodesOrPromise && !isContentNodePromise) {
		if (!hasExtensions(contentNodesOrPromise)) {
			return {};
		}
	}

	const noExtensionsSignal = new Promise<void>((resolve) => {
		if (isContentNodePromise)
			contentNodesOrPromise.then((contentNodes: any) => {
				if (!hasExtensions(contentNodes)) {
					resolve();
				}
			});
	});

	// NOTE: prealod is not a type. It was created with a typo https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/dynamic_configs/confluence_frontend_prealod_macro_timeout
	const macroTimeout = StatsigConfigurations.getNumber(
		'confluence_frontend_prealod_macro_timeout',
		SLOW_QUERY_DEFAULT_TIMEOUT,
	);

	const macrosQueryTask = () =>
		slowQueryGuard(
			'MacrosQuery_preload',
			() =>
				query<multiMacroQueryWithAppKeyQuery$data, multiMacroQueryWithAppKeyQuery$variables>({
					variables: {
						first: DEFAULT_PRELOAD_COUNT,
						contentId,
						blocklist,
					},
					query: MultiMacroQueryWithAppKeyQuery,
					context: { single: true },
				}),
			macroTimeout,
			noExtensionsSignal,
		).then(failureIfNotSuccess('timeout-rendered-macros'));

	const preloadResult = await macrosQueryTask();

	return preloadResult ? Object.assign({}, preloadResult) : {};
};

export async function preloadMacrosSSRMultiple(
	contentId: string,
	contentNodesOrPromise?: any,
): Promise<PreloadFailureResult | undefined> {
	const topXFailed = await preloadMacrosSSRAndReturnTopXFailed(contentId, contentNodesOrPromise);
	window['__SSR_MACRO_PRELOAD_TOPX_FAILS__'] = topXFailed;

	return topXFailed;
}
